import { Loader2, AlertTriangle, Image, Video, Zap, Calendar } from "lucide-react";
import { Button } from "@/components/flowly/infra/Button/Button";
import { useLanguage } from "@/contexts/LanguageContext";
import type { SubscriptionInfo } from "@/lib/api/payments";

interface ConfirmationStepProps {
  isLoading: boolean;
  subscriptionInfo: SubscriptionInfo | null;
  onConfirmCancel: () => void;
  onKeepSubscription: () => void;
}

export const ConfirmationStep = ({
  isLoading,
  subscriptionInfo,
  onConfirmCancel,
  onKeepSubscription,
}: ConfirmationStepProps) => {
  const { t, language } = useLanguage();
  const locale = language === "he" ? "he-IL" : "en-US";

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString(locale, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-destructive" />
        <h3 className="text-lg font-semibold text-foreground">
          {t.cancellation.whatYouLose}
        </h3>
      </div>

      {subscriptionInfo ? (
        <div className="rounded-xl border border-border divide-y divide-border">
          <div className="flex items-center gap-3 px-4 py-3">
            <Zap className="w-4 h-4 text-primary" />
            <span className="text-sm text-muted-foreground">
              {t.cancellation.creditsRemaining.replace(
                "{n}",
                String(subscriptionInfo.creditsRemaining),
              )}
            </span>
          </div>
          <div className="flex items-center gap-3 px-4 py-3">
            <Image className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {t.cancellation.imagesGenerated.replace(
                "{n}",
                String(subscriptionInfo.imagesGenerated),
              )}
            </span>
          </div>
          <div className="flex items-center gap-3 px-4 py-3">
            <Video className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {t.cancellation.videosGenerated.replace(
                "{n}",
                String(subscriptionInfo.videosGenerated),
              )}
            </span>
          </div>
          <div className="flex items-center gap-3 px-4 py-3">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {t.cancellation.accessUntil.replace(
                "{date}",
                formatDate(subscriptionInfo.currentPeriodEnd),
              )}
            </span>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-border divide-y divide-border">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3">
              <div className="w-4 h-4 rounded bg-muted animate-pulse" />
              <div className="h-4 w-32 rounded bg-muted animate-pulse" />
            </div>
          ))}
        </div>
      )}

      <p className="text-sm text-muted-foreground text-center">
        {t.cancellation.switchToFree}
      </p>

      {/* Keep subscription (primary) + Confirm cancel (de-emphasized) */}
      <div className="flex flex-col gap-2">
        <Button
          variant="primary"
          className="w-full"
          onClick={onKeepSubscription}
          disabled={isLoading}
        >
          {t.cancellation.keepSubscription}
        </Button>
        <Button
          variant="ghost"
          className="w-full text-destructive hover:text-destructive"
          onClick={onConfirmCancel}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            t.cancellation.confirmCancel
          )}
        </Button>
      </div>
    </div>
  );
};
